import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export const NETWORKS = [
  { value: "MTN", label: "MTN MoMo" },
  { value: "Airtel", label: "Airtel Money" },
  { value: "Zamtel", label: "Zamtel Kwacha" },
];

export function MomoPaymentForm({ invoice, onPending, onNotConfigured }) {
  const [network, setNetwork] = useState("MTN");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { fmt } = useCurrency();

  const balance = invoice ? invoice.total - invoice.amount_paid : 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/mobile-money", {
        invoice_id: invoice.id, network, phone_number: phone, full_name: name, email,
      });
      if (data.redirect_url) {
        window.location.assign(data.redirect_url);
        return;
      }
      onPending(data.tx_ref, phone, network);
    } catch (err) {
      if (err?.response?.status === 503) {
        onNotConfigured();
      } else {
        toast.error(formatApiError(err));
      }
      setBusy(false);
    }
  };

  return (
    <>
      <div className="border border-border bg-secondary/50 px-4 py-3 text-sm" data-testid="momo-amount">
        Amount due: <span className="font-heading text-lg font-bold">{fmt(balance)}</span>
        <span className="ml-2 text-xs text-muted-foreground">charged in ZMW</span>
      </div>
      <form onSubmit={submit} className="mt-2 space-y-4">
        <div className="space-y-1.5">
          <Label>Network</Label>
          <RadioGroup value={network} onValueChange={setNetwork} className="flex gap-3" data-testid="momo-network">
            {NETWORKS.map((n) => (
              <label key={n.value} data-testid={`momo-network-${n.value.toLowerCase()}`}
                className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-sm border px-3 py-2.5 text-sm font-medium transition-colors ${
                  network === n.value ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"
                }`}>
                <RadioGroupItem value={n.value} className="sr-only" />
                {n.label}
              </label>
            ))}
          </RadioGroup>
        </div>
        <div className="space-y-1.5">
          <Label>Guest mobile money number</Label>
          <Input data-testid="momo-phone" placeholder="26097XXXXXXX" pattern="260[0-9]{9}" value={phone}
            onChange={(e) => setPhone(e.target.value)} required />
          <p className="text-[11px] text-muted-foreground">Format: 260 followed by 9 digits — the number linked to the wallet.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Guest full name</Label>
          <Input data-testid="momo-name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        </div>
        <div className="space-y-1.5">
          <Label>Guest email (optional)</Label>
          <Input type="email" data-testid="momo-email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <Button type="submit" disabled={busy} data-testid="momo-submit" className="w-full transition-colors">
          {busy ? "Sending prompt…" : `Send ${network} payment prompt`}
        </Button>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <CheckCircle size={13} className="shrink-0 text-status-confirmed" />
          Guest approves with their wallet PIN on their own phone — never on this screen.
        </div>
      </form>
    </>
  );
}
